import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Crown, Mail } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { useMeusConvites, useResponderConvite } from "@/core/hooks/useConvites";
import { useAjudaPapel } from "@/core/hooks/useAjudaPapel";
import { useMinisteriosQueLidero } from "@/core/hooks/useMinisterios";
import type { ConviteMinisterio } from "@/core/services/convitesService";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { PreviewBibliotecaHome } from "@/modules/biblioteca/components/PreviewBibliotecaHome";
import { CartaoLivroEmMaos } from "@/modules/biblioteca/components/CartaoLivroEmMaos";
import { PreviewComunicadosHome } from "@/modules/comunicados/components/PreviewComunicadosHome";
import { PreviewEscalaHome } from "@/modules/escala/components/PreviewEscalaHome";
import { PreviewAgendaHome } from "@/modules/calendario/components/PreviewAgendaHome";
import { PreviewAniversariantesHome } from "@/core/components/PreviewAniversariantesHome";
import { ConviteAjudaInicio } from "@/core/components/ConviteAjudaInicio";
import { ConviteAjudaPapel } from "@/core/components/ConviteAjudaPapel";
import { CartaoElosQueGerencio } from "@/core/components/CartaoElosQueGerencio";
import { BoasVindasAjuda } from "@/core/components/BoasVindasAjuda";
import { CartaoPermissaoNotificacao } from "@/core/components/CartaoPermissaoNotificacao";
import { dataPorExtenso, saudacaoDoDia } from "@/core/utils/saudacao";

const CARD_CLASSE =
  "group flex items-start gap-4 rounded-2xl border border-border bg-surface p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-md";

/**
 * Home: tela de PREVIEW, e só preview (pedido do usuário) — traz um resumo
 * rápido do que importa (convites pendentes, governança de ministério
 * liderado, previews de Biblioteca e Calendário). Navegar para a lista de
 * módulos ou para o Núcleo agora é sempre pela barra fixa inferior
 * (`BottomTabBar` — abas Módulos/Núcleo), não mais daqui.
 *
 * Regra permanente (Sprint 3): antes de dar a um módulo novo um preview
 * nesta tela, perguntar ao usuário se ele quer isso e o que deve aparecer.
 *
 * Aprovado em 02/11/2026 (protótipo "Início com mais vida"): cumprimento com a hora e a data, e o
 * cartão "Seu livro em mãos". Ficaram para depois: livro para ler agora, meta de leitura,
 * novidades da Biblioteca e palavra da semana (precisam de aprovação própria).
 *
 * Regra permanente (07/10/2026): preview sem dado para mostrar (nenhum
 * comunicado, nenhuma escala, nada agendado, ninguém aniversariando, Biblioteca
 * com tudo zerado) não aparece — nem enquanto carrega. Cada preview decide isso sozinho.
 */
export function Inicio() {
  const { pessoa, ehAdminPlataforma } = useAuth();
  const navigate = useNavigate();

  const primeiroNome = pessoa?.nome_completo?.split(" ")[0];
  const agora = new Date();
  const saudacao = saudacaoDoDia(agora);

  // Convites de ministério pendentes (DA-003) — respondidos aqui mesmo.
  const { data: convites } = useMeusConvites();
  const responder = useResponderConvite();
  const [erroConvite, setErroConvite] = useState<string | null>(null);
  const [conviteParaRecusar, setConviteParaRecusar] = useState<ConviteMinisterio | null>(null);
  // Ministérios que a pessoa lidera (DA-004): porta de entrada do líder não-admin.
  const { data: lideranca } = useMinisteriosQueLidero();
  const ministeriosLiderados = ehAdminPlataforma ? [] : (lideranca ?? []);
  // Gestor de elo: o cartão "Elos que você gerencia" fica ao lado do "Meu ministério".
  const { papel } = useAjudaPapel();

  async function handleResponderConvite(convite: ConviteMinisterio, aceitar: boolean) {
    setErroConvite(null);
    try {
      await responder.mutateAsync({ vinculoId: convite.vinculoId, aceitar });
    } catch (e) {
      setErroConvite(e instanceof Error ? e.message : "Não foi possível responder o convite.");
    } finally {
      setConviteParaRecusar(null);
    }
  }

  return (
    <main className="mx-auto w-full max-w-4xl px-4 pb-16 pt-6 sm:px-6 sm:pt-10">
      <header className="mb-6">
        <h1 className="text-2xl font-black tracking-tight text-primary sm:text-3xl">
          {primeiroNome ? `${saudacao}, ${primeiroNome}!` : `${saudacao}!`}
        </h1>
        <p className="mt-1 text-sm text-muted">{dataPorExtenso(agora)}</p>
      </header>

      {/* Convites pendentes (DA-003): destaque no topo, impossível não ver. */}
      {!!convites?.length && (
        <section className="mb-6 space-y-3">
          {erroConvite && (
            <p role="alert" className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">
              {erroConvite}
            </p>
          )}
          {convites.map((c) => (
            <div
              key={c.vinculoId}
              className="flex flex-col gap-3 rounded-2xl border border-primary/40 bg-primary/5 p-4 sm:flex-row sm:items-center sm:p-5"
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                <Mail size={22} />
              </span>
              <p className="min-w-0 flex-1 text-sm text-text">
                Você foi convidado(a) para o ministério{" "}
                <span className="font-bold">{c.ministerioNome}</span> como{" "}
                <span className="font-bold">{c.funcaoNome}</span>.
              </p>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  onClick={() => handleResponderConvite(c, true)}
                  disabled={responder.isPending}
                  className="rounded-lg bg-primary px-4 py-2 text-xs font-bold text-on-primary transition-colors hover:bg-primary-hover disabled:opacity-60"
                >
                  Aceitar
                </button>
                <button
                  type="button"
                  onClick={() => setConviteParaRecusar(c)}
                  disabled={responder.isPending}
                  className="rounded-lg border border-border bg-surface px-4 py-2 text-xs font-bold text-muted transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-60"
                >
                  Recusar
                </button>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Pedido de permissão de notificações (aprovado em 02/11/2026, protótipo "Pedir permissão"):
          cartão que só convida, no topo; some para quem já decidiu. Ver CartaoPermissaoNotificacao. */}
      <CartaoPermissaoNotificacao />

      {/* Ajuda do Elos (aprovado em 15/10/2026): boas-vindas por cima do Início no primeiro acesso
          (pode ser pulada) e, depois, o cartão "Novo por aqui?", que fecha com "Agora não" e some
          sozinho quando as lições foram vistas. */}
      <BoasVindasAjuda />
      <ConviteAjudaInicio />
      {/* Aprovado em 02/11/2026 (protótipo "Ajuda: ministérios, funções e gestão", v2): para quem
          lidera ou é Gestor de elo, um cartão que convida para a lição do papel. Mesmo jeito do
          "Novo por aqui?": só convida, fecha com "Agora não" e some quando a lição é vista. */}
      <ConviteAjudaPapel />

      {/* Comunicados no topo (decisão de produto, 06/10/2026): os 2 mais recentes
          que a pessoa pode ver; a seção some quando não há nenhum. */}
      <PreviewComunicadosHome />

      {/* Minhas Escalas (pedido do usuário, 07/10/2026): só as escalas em que
          a pessoa está na equipe, com o status da própria resposta; some se não houver. */}
      <PreviewEscalaHome />

      {/* Previews da Home (Sprint 3): Biblioteca (papel-aware) + agenda do Calendário. */}
      <PreviewBibliotecaHome />
      <CartaoLivroEmMaos />
      <PreviewAgendaHome />
      {/* Aniversariantes da Semana (pedido do usuário, 26/07/2026): só dia/mês,
          nunca idade ou ano de nascimento — ver migration 0026. */}
      <PreviewAniversariantesHome />

      {/* Governança do líder (DA-004): quem lidera e não é admin. O Gestor de elo ganha, ao lado,
          o cartão "Elos que você gerencia" (aprovado em 02/11/2026). */}
      {(ministeriosLiderados.length > 0 || papel.gestor) && (
        <div className="mb-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {ministeriosLiderados.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => navigate(`/ministerios/${m.id}`)}
              className={CARD_CLASSE}
            >
              {/* J4 (revisão 20/07/2026): emoji genérico trocado pelo mesmo ícone de
                  "Crown" usado em GerenciarMinisterios.tsx para líder — consistência
                  visual entre as duas telas. */}
              <span
                aria-hidden="true"
                className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-warning/10 text-warning"
              >
                <Crown size={22} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] font-bold uppercase tracking-wider text-warning">
                  Meu ministério
                </span>
                <span className="block font-bold text-text">{m.nome}</span>
                <span className="mt-1 block text-sm text-muted">
                  Você lidera este ministério: convide integrantes e defina funções.
                </span>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary">
                  Gerenciar
                  <ChevronRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </span>
            </button>
          ))}
          {papel.gestor && <CartaoElosQueGerencio codigosElos={papel.elos} />}
        </div>
      )}

      <ModalConfirmacao
        aberto={!!conviteParaRecusar}
        titulo="Recusar convite"
        mensagem={`Recusar o convite para o ministério "${conviteParaRecusar?.ministerioNome ?? ""}"? Você não entrará nele.`}
        textoBotaoConfirmar="Recusar"
        carregando={responder.isPending}
        onConfirmar={() => {
          if (conviteParaRecusar) void handleResponderConvite(conviteParaRecusar, false);
        }}
        onCancelar={() => setConviteParaRecusar(null)}
      />
    </main>
  );
}
